import { useEffect, useRef, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setQbConnected } from "./store/actions/user";
import apiService from "./services/api";

const QbCallback = () => {
  const dispatch = useDispatch();
  const hasExchangedToken = useRef(false);
  const [status, setStatus] = useState('processing');
  const [countdown, setCountdown] = useState(5);
  const [debugInfo, setDebugInfo] = useState({});

  // Get user from Redux store
  const user = useSelector((state) => state.user);

  // Memoize query parameters to prevent new references on every render
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      state: params.get("state"),
      code: params.get("code"),
      company_id: params.get("realmId"),
    };
  }, []);

  const { state, code, company_id } = queryParams;

  // Redirect to main app
  const redirectToApp = () => {
    const appUrl = process.env.REACT_APP_MAIN_APP_URL || window.location.origin;
    
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage({
          type: 'QB_AUTH_SUCCESS',
          data: { company_id, success: true }
        }, appUrl);
        
        setTimeout(() => {
          window.close();
          if (!window.closed) {
            window.location.href = appUrl;
          }
        }, 1000);
        
      } catch (error) {
        console.error('Error communicating with parent window:', error);
        window.location.href = appUrl;
      }
    } else {
      window.location.href = appUrl;
    }
  };

  // Function to exchange tokens (add this before useEffect)
  const exchangeTokens = () => {
    if (hasExchangedToken.current) return;
    hasExchangedToken.current = true;

    console.log("Exchanging token with QB...", { code, company_id, state });

    apiService
      .post("/api/qb/exchange-token", {
        code,
        company_id,
        state,
      })
      .then((data) => {
        console.log("Token exchange response:", data);
        dispatch(setQbConnected(true));
        setStatus('success');
        
        // Start countdown timer
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              redirectToApp();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
      })
      .catch((error) => {
        console.error("Error exchanging token:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.status,
          response: error.response
        });
        
        setStatus('error');
        hasExchangedToken.current = false;
        
        setDebugInfo(prev => ({
          ...prev,
          error: error.message,
          errorStatus: error.status
        }));
        
        setTimeout(redirectToApp, 5000);
      });
  };

  useEffect(() => {
    const restoreAuthenticationAndProceed = () => {
      // Get tokens from various sources
      let authToken = localStorage.getItem('auth_token');
      const reduxToken = user?.token;
      const isLoggedIn = user?.loggedIn;
      
      // FIRST: Try to extract auth token from state parameter if present
      if (state && state.includes('|')) {
        const [originalState, stateToken] = state.split('|');
        if (stateToken && stateToken !== 'undefined' && stateToken !== 'null') {
          authToken = stateToken;
          localStorage.setItem('auth_token', authToken);
          console.log('Restored auth token from state parameter');
        }
      }
      
      // SECOND: Try to restore from Redux if localStorage is empty
      if (!authToken && reduxToken) {
        console.log('Restoring token to localStorage from Redux');
        localStorage.setItem('auth_token', reduxToken);
        authToken = reduxToken;
      }
      
      // THIRD: Try to get from parent window if opened as popup
      if (!authToken && window.opener && !window.opener.closed) {
        try {
          const parentToken = window.opener.localStorage.getItem('auth_token');
          if (parentToken) {
            localStorage.setItem('auth_token', parentToken);
            authToken = parentToken;
            console.log('Restored auth token from parent window');
          }
        } catch (e) {
          console.log('Cannot access parent window storage:', e);
        }
      }

      const debug = {
        hasCode: !!code,
        hasCompanyId: !!company_id,
        localStorageToken: !!authToken,
        reduxToken: !!reduxToken,
        isLoggedIn: isLoggedIn,
        userEmail: user?.email || 'none',
        tokenMatch: authToken === reduxToken,
        stateHasToken: state && state.includes('|'),
        authTokenSource: authToken ? (
          state && state.includes('|') ? 'state_parameter' :
          reduxToken ? 'redux' : 'localStorage'
        ) : 'none'
      };
      
      setDebugInfo(debug);
      console.log('QB Callback Debug Info:', debug);

      // apiService automatically handles auth headers via getUserToken() method
      // Just proceed with token exchange if we have all required parameters
      if (authToken && code && company_id) {
        exchangeTokens();
      } else {
        console.error('Missing required parameters:', { 
          hasToken: !!authToken, 
          hasCode: !!code, 
          hasCompanyId: !!company_id 
        });
        setStatus('error');
        setDebugInfo(prev => ({
          ...prev,
          error: 'Missing authentication token or required parameters'
        }));
        setTimeout(redirectToApp, 3000);
      }
    };

    // Only run once when component mounts and we have the necessary data
    if (code && code.length && !hasExchangedToken.current) {
      restoreAuthenticationAndProceed();
    } else if (!code) {
      console.error('No authorization code found in URL');
      setStatus('error');
      setTimeout(redirectToApp, 3000);
    }
  }, [code, company_id, state, user?.token]); // Added user?.token to dependencies

  // Manual close/redirect button
  const handleManualRedirect = () => {
    redirectToApp();
  };

  if (status === 'processing') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Connecting to QuickBooks...</h3>
        <p>Please wait while we complete the setup.</p>
        
        {/* DEBUG INFO - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ marginTop: '20px', fontSize: '12px', textAlign: 'left', background: '#f5f5f5', padding: '10px' }}>
            <strong>Debug Info:</strong>
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
          </div>
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Connection Error</h3>
        <p>There was an issue connecting to QuickBooks. Redirecting you back...</p>
        
        {/* DEBUG INFO - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ marginTop: '20px', fontSize: '12px', textAlign: 'left', background: '#ffe6e6', padding: '10px' }}>
            <strong>Error Debug Info:</strong>
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
          </div>
        )}
        
        <button onClick={handleManualRedirect} style={{ 
          marginTop: '10px', 
          padding: '10px 20px',
          cursor: 'pointer'
        }}>
          Return to App
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h3>✅ QuickBooks Connected Successfully!</h3>
      <p>Redirecting you back to the app in {countdown} seconds...</p>
      
      {/* DEBUG INFO - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ marginTop: '20px', fontSize: '12px', textAlign: 'left', background: '#e6ffe6', padding: '10px' }}>
          <strong>Success Debug Info:</strong>
          <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
        </div>
      )}
      
      <button onClick={handleManualRedirect} style={{ 
        marginTop: '10px', 
        padding: '10px 20px',
        cursor: 'pointer'
      }}>
        Return to App Now
      </button>
    </div>
  );
};

export default QbCallback;